import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Building2, Eye, Loader2, Mail, MapPin, Phone, Trash2, Upload, UserRound, CheckCircle2, XCircle, Download, Activity } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useGetDashboardOrderDetailsQuery,
  useUpdateOrderStatusMutation,
  useLazyGetDashboardOrderAuthorizationPdfQuery,
  type DashboardOrderTestDetail,
} from "@/api/dashboardApi";
import {
  useDeleteVisitorTestResultDocumentMutation,
  useUploadVisitorTestResultDocumentMutation,
  useUpdateVisitorTestWorkflowStatusMutation,
  useLazyGetVisitorTestResultDocumentQuery,
} from "@/api/visitorTestApi";
import { formatUsDate, formatUsDateTime } from "@/utils/date";
import { useSelector } from "react-redux";
import type { RootState } from "@/store";

const formatDateTime = (value?: string | null) => {
  return formatUsDateTime(value);
};

const formatDateOnly = (value?: string | null) => {
  return formatUsDate(value);
};

const formatCurrency = (amount?: number | string | null, currencyCode?: string | null) => {
  if (amount == null) return "-";
  const numeric = typeof amount === "number" ? amount : Number(amount);
  if (!Number.isFinite(numeric)) return "-";
  if (!currencyCode || currencyCode === "MIXED") {
    return `${numeric.toFixed(2)}${currencyCode === "MIXED" ? " (Mixed Currency)" : ""}`;
  }
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numeric);
  } catch {
    return `${numeric.toFixed(2)} ${currencyCode}`;
  }
};

const getStatusColor = (status?: string | null) => {
  switch ((status || "").toUpperCase()) {
    case "SCHEDULED":
    case "PENDING":
      return "bg-yellow-100 text-yellow-800";
    case "IN_PROGRESS":
    case "CONFIRMED":
      return "bg-blue-100 text-blue-800";
    case "COMPLETED":
    case "READY_TO_REVIEW":
      return "bg-green-100 text-green-800";
    case "CANCELLED":
    case "CANCELED":
      return "bg-red-100 text-red-800";
    case "NO_SHOW":
      return "bg-orange-100 text-orange-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

type TestWorkflowStatus =
  | "WAITING_FOR_VISIT"
  | "WAITING_FOR_RESULT"
  | "IN_PROGRESS"
  | "COMPLETED"
  | "DID_NOT_PERFORM"
  | "RESULT_UPLOADED";

const getTestWorkflowStatus = (test: DashboardOrderTestDetail): TestWorkflowStatus => {
  const appointmentStatus = (test.appointmentStatus || "").toUpperCase();
  const visitorTestStatus = (test.visitorTestStatus || "").toUpperCase();

  if (test.resultDocumentUrl || visitorTestStatus === "RESULT_UPLOADED") {
    return "RESULT_UPLOADED";
  }

  // Check SCHEDULED first — newly submitted requests should show as "Waiting for Visit" (Scheduled)
  // not fall through to DID_NOT_PERFORM from stale cancelled/no-show data
  if (appointmentStatus === "SCHEDULED") {
    if (visitorTestStatus === "READY_TO_REVIEW") {
      return "COMPLETED";
    }
    if (visitorTestStatus === "WAITING_FOR_RESULT") {
      return "WAITING_FOR_RESULT";
    }
    return "WAITING_FOR_VISIT";
  }

  if (appointmentStatus === "CANCELLED" || appointmentStatus === "NO_SHOW" || visitorTestStatus === "CANCELED") {
    return "DID_NOT_PERFORM";
  }

  if (visitorTestStatus === "READY_TO_REVIEW") {
    return "COMPLETED";
  }

  if (visitorTestStatus === "WAITING_FOR_RESULT" || appointmentStatus === "COMPLETED") {
    return "WAITING_FOR_RESULT";
  }

  if (appointmentStatus === "IN_PROGRESS") {
    return "IN_PROGRESS";
  }

  return "WAITING_FOR_VISIT";
};

const getTestWorkflowStatusLabel = (status: TestWorkflowStatus) => {
  switch (status) {
    case "RESULT_UPLOADED":
      return "Result Uploaded";
    case "WAITING_FOR_RESULT":
      return "Waiting for Result";
    case "COMPLETED":
      return "Completed";
    case "IN_PROGRESS":
      return "In Progress";
    case "DID_NOT_PERFORM":
      return "Did Not Perform";
    case "WAITING_FOR_VISIT":
    default:
      return "Waiting for Visit";
  }
};

const getTestWorkflowStatusColor = (status: TestWorkflowStatus) => {
  switch (status) {
    case "RESULT_UPLOADED":
      return "bg-emerald-100 text-emerald-800";
    case "WAITING_FOR_RESULT":
      return "bg-blue-100 text-blue-800";
    case "COMPLETED":
      return "bg-cyan-100 text-cyan-800";
    case "IN_PROGRESS":
      return "bg-indigo-100 text-indigo-800";
    case "DID_NOT_PERFORM":
      return "bg-rose-100 text-rose-800";
    case "WAITING_FOR_VISIT":
    default:
      return "bg-amber-100 text-amber-800";
  }
};

export default function DashboardOrderDetails() {
  const { user } = useSelector((state: RootState) => state.auth);
  const isClientRole = user?.role === "CLIENT" || user?.role === "CLIENT_EMPLOYEE";

  const { appointmentId } = useParams();

  const parsedAppointmentId = Number(appointmentId);
  const isValidId = Number.isFinite(parsedAppointmentId) && parsedAppointmentId > 0;

  const [selectedFiles, setSelectedFiles] = useState<Record<number, File | null>>({});
  const [savingAppointmentId, setSavingAppointmentId] = useState<number | null>(null);
  const [statusDraft, setStatusDraft] = useState<string>("");

  const {
    data: orderDetail,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetDashboardOrderDetailsQuery(
    {
      appointmentId: parsedAppointmentId,
      role: user?.role,
      clientId: user?.clientId
    },
    { skip: !isValidId }
  );

  const [uploadVisitorTestResultDocument] = useUploadVisitorTestResultDocumentMutation();
  const [updateVisitorTestWorkflowStatus] = useUpdateVisitorTestWorkflowStatusMutation();
  const [updateOrderStatus, updateOrderStatusResult] = useUpdateOrderStatusMutation();
  const [deleteVisitorTestResultDocument] = useDeleteVisitorTestResultDocumentMutation();
  const [getVisitorTestResultDocument] = useLazyGetVisitorTestResultDocumentQuery();
  const [downloadPdf, { isLoading: isDownloadingPdf }] = useLazyGetDashboardOrderAuthorizationPdfQuery();

  useEffect(() => {
    setStatusDraft(orderDetail?.orderStatus || "");
  }, [orderDetail?.orderStatus]);

  const handleDownloadPdf = async () => {
    if (!orderDetail?.appointmentId) return;
    try {
      const blob = await downloadPdf(orderDetail.appointmentId).unwrap();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `medigenix-authorization-${orderDetail.appointmentId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (e) {
      console.error("Failed to download PDF", e);
      toast.error("Failed to download authorization form");
    }
  };

  const handleFileChange = (appointmentIdValue: number, file: File | null) => {
    setSelectedFiles((prev) => ({
      ...prev,
      [appointmentIdValue]: file,
    }));
  };

  const openDocumentPreview = async (test: DashboardOrderTestDetail) => {
    if (!test.visitorTestId) {
      toast.error("Document record not found.");
      return;
    }
    try {
      const blob = await getVisitorTestResultDocument(test.visitorTestId).unwrap();
      const objectUrl = URL.createObjectURL(blob);
      
      // Open in new tab instead of popup
      window.open(objectUrl, "_blank");
      
      // Auto-revoke after some time to avoid memory leaks, 
      // since we can't easily track when the new tab is closed.
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
      
    } catch (error) {
      console.error("Failed to load document preview:", error);
      toast.error("Unable to load uploaded document.");
    }
  };

  const isAllowedFile = (file: File) => {
    const contentType = (file.type || "").toLowerCase();
    if (contentType === "application/pdf" || contentType.startsWith("image/")) {
      return true;
    }
    const lowerName = file.name.toLowerCase();
    return [".pdf", ".png", ".jpg", ".jpeg", ".gif", ".webp"].some((ext) =>
      lowerName.endsWith(ext)
    );
  };

  const handleUploadResult = async (test: DashboardOrderTestDetail) => {
    if (!orderDetail) return;
    if (!test.testTypeId) {
      toast.error("Test type is missing for this row. Please verify clinic-test mapping.");
      return;
    }

    const file = selectedFiles[test.appointmentId];
    if (!file) {
      toast.error("Please select a PDF or image file before upload.");
      return;
    }
    if (!isAllowedFile(file)) {
      toast.error("Only PDF and image files are allowed.");
      return;
    }

    try {
      setSavingAppointmentId(test.appointmentId);
      const formData = new FormData();
      if (test.visitorTestId) {
        formData.append("visitorTestId", String(test.visitorTestId));
      }
      formData.append("appointmentId", String(test.appointmentId));
      formData.append("visitorId", String(orderDetail.userId));
      formData.append("testTypeId", String(test.testTypeId));
      formData.append("testName", test.testName || "Unknown Test");
      if (orderDetail.clientId !== undefined && orderDetail.clientId !== null) {
        formData.append("clientId", String(orderDetail.clientId));
      }
      if (orderDetail.clientName) {
        formData.append("clientName", orderDetail.clientName);
      }
      if (test.scheduledAt) {
        formData.append("scheduledDate", test.scheduledAt);
      }
      formData.append("file", file);

      await uploadVisitorTestResultDocument(formData).unwrap();

      toast.success("Result uploaded successfully");
      handleFileChange(test.appointmentId, null);
      await refetch();
    } catch (uploadError) {
      console.error("Failed to upload result document:", uploadError);
      toast.error("Failed to upload result document");
    } finally {
      setSavingAppointmentId(null);
    }
  };

  const handleDeleteResult = async (test: DashboardOrderTestDetail) => {
    if (!test.visitorTestId) {
      toast.error("Uploaded document record not found.");
      return;
    }
    const confirmed = window.confirm("Remove uploaded document for this test?");
    if (!confirmed) return;
    try {
      await deleteVisitorTestResultDocument(test.visitorTestId).unwrap();
      toast.success("Uploaded document removed.");
      handleFileChange(test.appointmentId, null);
      await refetch();
    } catch (deleteError) {
      console.error("Failed to delete uploaded result document:", deleteError);
      toast.error("Failed to remove uploaded document");
    }
  };

  const handleManualTestWorkflowStatus = async (
    test: DashboardOrderTestDetail,
    status: "IN_PROGRESS" | "READY_TO_REVIEW" | "CANCELED"
  ) => {
    if (!orderDetail) return;
    if (!test.testTypeId) {
      toast.error("Test type is missing for this row.");
      return;
    }
    try {
      setSavingAppointmentId(test.appointmentId);
      await updateVisitorTestWorkflowStatus({
        visitorTestId: test.visitorTestId ?? undefined,
        visitorId: orderDetail.userId,
        appointmentId: test.appointmentId,
        testTypeId: test.testTypeId,
        testName: test.testName || "Unknown Test",
        clientId: orderDetail.clientId ?? undefined,
        clientName: orderDetail.clientName || undefined,
        scheduledDate: test.scheduledAt || undefined,
        status,
      }).unwrap();
      toast.success(
        status === "CANCELED" ? "Test marked as Did Not Perform." : "Test marked as Completed."
      );
      await refetch();
    } catch (statusError) {
      console.error("Failed to update test workflow status:", statusError);
      toast.error("Failed to update test status.");
    } finally {
      setSavingAppointmentId(null);
    }
  };

  const handleSaveStatus = async () => {
    if (!orderDetail) return;
    if (!statusDraft || statusDraft === orderDetail.orderStatus) return;
    try {
      await updateOrderStatus({
        appointmentId: orderDetail.appointmentId,
        status: statusDraft,
        role: user?.role,
        clientId: user?.clientId
      }).unwrap();
      toast.success("Order status updated.");
      await refetch();
    } catch (statusError) {
      console.error("Failed to update order status:", statusError);
      toast.error("Failed to update order status");
    }
  };

  if (!isValidId) {
    return (
      <div className="space-y-4">
        <Button variant="outline" asChild>
          <Link to={`${isClientRole ? '/client' : '/super-admin'}/dashboard`}>Back to Dashboard</Link>
        </Button>
        <Card>
          <CardContent className="py-8">
            <p className="text-sm text-red-600">Invalid order ID.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-14">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  if (error || !orderDetail) {
    return (
      <div className="space-y-4">
        <Button variant="outline" asChild>
          <Link to={`${isClientRole ? '/client' : '/super-admin'}/dashboard`}>Back to Dashboard</Link>
        </Button>
        <Card>
          <CardContent className="py-8">
            <p className="text-sm text-red-600">Failed to load order details.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" asChild className="gap-2 text-slate-500 hover:text-slate-800">
            <Link to={`${isClientRole ? '/client' : '/super-admin'}/dashboard`}>
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Order Details #{orderDetail.appointmentId}</h1>
            <p className="text-sm text-muted-foreground">
              Upload and view test results for this order.
            </p>
          </div>
        </div>
        {isFetching && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Order Summary</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">

          <div className={`grid grid-cols-1 ${isClientRole ? "xl:grid-cols-2" : "xl:grid-cols-3"} gap-4`}>
            <div className="rounded-xl border bg-white shadow-sm overflow-hidden">
              <div className="border-b bg-slate-50 px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">Visitor Details</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 text-sm">
                <div className="px-4 py-3 border-b sm:border-r">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Visitor Name</p>
                  <p className="font-semibold mt-1">{orderDetail.candidateName || "-"}</p>
                </div>
                <div className="px-4 py-3 border-b">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Date of Birth</p>
                  <p className="font-semibold mt-1">{formatDateOnly(orderDetail.dateOfBirth)}</p>
                </div>
                <div className="px-4 py-3 border-b sm:border-r">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Email</p>
                  <p className="font-semibold mt-1 break-all">{orderDetail.email || "-"}</p>
                </div>
                <div className="px-4 py-3 border-b">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Phone</p>
                  <p className="font-semibold mt-1">{orderDetail.phoneNumber || "-"}</p>
                </div>
                <div className="px-4 py-3 border-b sm:border-r">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Gender</p>
                  <p className="font-semibold mt-1">{orderDetail.visitorGender || "-"}</p>
                </div>
                <div className="px-4 py-3 border-b">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">ZIP Code</p>
                  <p className="font-semibold mt-1">{orderDetail.visitorZipCode || "-"}</p>
                </div>
                <div className="px-4 py-3 border-b sm:border-r">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">SSN</p>
                  <p className="font-semibold mt-1">{orderDetail.visitorSsnMasked || "-"}</p>
                </div>
                <div className="px-4 py-3 border-b">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Client</p>
                  <p className="font-semibold mt-1">{orderDetail.clientName || "-"}</p>
                </div>
                <div className="px-4 py-2 sm:col-span-2">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Address</p>
                  <p
                    className="font-semibold mt-1 truncate"
                    title={orderDetail.visitorAddress || "Address not provided"}
                  >
                    {orderDetail.visitorAddress || "Address not provided"}
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-xl border bg-white shadow-sm overflow-hidden">
              <div className="border-b bg-slate-50 px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">Clinic Details</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 text-sm">
                <div className="px-4 py-3 border-b sm:border-r">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">System Order ID</p>
                  <p className="font-semibold mt-1">{orderDetail.appointmentId || "-"}</p>
                </div>
                <div className="px-4 py-3 border-b">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Order Number</p>
                  <p className="font-semibold mt-1">{orderDetail.orderNumber || "-"}</p>
                </div>
                <div className="px-4 py-3 border-b sm:border-r">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Order Submitted By</p>
                  <p className="font-semibold mt-1">{orderDetail.orderSubmittedBy || "-"}</p>
                </div>
                <div className="px-4 py-3 border-b">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Visit Date</p>
                  <p className="font-semibold mt-1">{formatDateTime(orderDetail.visitDate)}</p>
                </div>
                {orderDetail.employeeId && (
                  <div className="px-4 py-3 border-b sm:border-r">
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Employee ID</p>
                    <p className="font-semibold mt-1">{orderDetail.employeeId}</p>
                  </div>
                )}
                <div className={`px-4 py-3 ${orderDetail.employeeId ? "border-b" : "sm:col-span-2"}`}>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Clinic</p>
                  <p className="font-semibold mt-1">{orderDetail.clinicName || "-"}</p>
                </div>
                <div className="px-4 py-2 sm:col-span-2">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Address / Phone</p>
                  <div className="mt-1 space-y-1">
                    <p
                      className="text-slate-700 inline-flex items-center gap-1 max-w-full"
                      title={orderDetail.clinicAddress || "Address not provided"}
                    >
                      <MapPin className="w-4 h-4 text-slate-500 shrink-0" />
                      <span className="truncate">{orderDetail.clinicAddress || "Address not provided"}</span>
                    </p>
                    {orderDetail.clinicPhone && (
                      <p className="text-slate-700 inline-flex items-center gap-1 max-w-full">
                        <Phone className="w-4 h-4 text-slate-500 shrink-0" />
                        <span className="font-semibold">{orderDetail.clinicPhone}</span>
                      </p>
                    )}
                  </div>
                </div>
                {!isClientRole && (
                  <div className="px-4 py-2 sm:col-span-2 border-t bg-slate-50/50">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Appointment Requirements</p>
                        <p className="font-semibold mt-1 text-slate-700">
                          {orderDetail.vendorAppointmentRequirements || "None specified"}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wide">COC Requirements</p>
                        <p className="font-semibold mt-1 text-slate-700">
                          {orderDetail.vendorCocRequirements || "None specified"}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                <div className="px-4 py-3 sm:col-span-2 border-t flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-blue-50/30">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Medigenix Authorization</p>
                    {orderDetail.acceptsMedigenixForm ? (
                      <div className="flex items-center gap-1.5 mt-1 text-emerald-700 font-medium text-sm">
                        <CheckCircle2 className="w-4 h-4" />
                        <span>Needed (Accepts Form)</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 mt-1 text-slate-500 font-medium text-sm">
                        <XCircle className="w-4 h-4" />
                        <span>Not Needed</span>
                      </div>
                    )}
                  </div>
                  {orderDetail.acceptsMedigenixForm && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleDownloadPdf}
                      disabled={isDownloadingPdf}
                      className="gap-2 text-blue-600 border-blue-200 bg-white hover:bg-blue-50 hover:text-blue-700 w-full sm:w-auto"
                    >
                      <Download className="w-4 h-4" />
                      {isDownloadingPdf ? 'Generating...' : 'Download Form'}
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {!isClientRole && (
              <div className="rounded-xl border bg-gradient-to-b from-blue-50/70 via-white to-white shadow-sm overflow-hidden">
                <div className="border-b bg-blue-100/50 px-4 py-3 flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-blue-700" />
                  <p className="text-xs font-semibold uppercase tracking-wide text-blue-900">Vendor Details</p>
                </div>
                <div className="space-y-4 p-4 text-sm">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Vendor Name</p>
                      <p className="font-semibold mt-1">{orderDetail.vendorName || "-"}</p>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Vendor ID</p>
                      <p className="font-semibold mt-1">{orderDetail.vendorId || "-"}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Contact Person</p>
                      <div className="flex items-center gap-2 mt-1">
                        <UserRound className="w-3.5 h-3.5 text-slate-400" />
                        <p className="font-semibold">{orderDetail.vendorContactName || "-"}</p>
                      </div>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Phone</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Phone className="w-3.5 h-3.5 text-slate-400" />
                        <p className="font-semibold">{orderDetail.vendorPhoneNumber || "-"}</p>
                      </div>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 sm:col-span-2">
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Email</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Mail className="w-3.5 h-3.5 text-slate-400" />
                        <p className="font-semibold truncate">{orderDetail.vendorEmailAddress || "-"}</p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-3">
                    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Full Address</p>
                      <p className="font-semibold mt-1">{orderDetail.vendorFullAddress || "-"}</p>
                    </div>
                  </div>

                  {orderDetail.totalCost != null && (
                    <div className="mt-2 rounded-xl bg-blue-600 p-4 text-white shadow-inner">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-bold uppercase tracking-widest text-blue-100">Order Totals</p>
                        <Badge className="bg-white/20 text-white border-0 hover:bg-white/30 capitalize">
                          {orderDetail.currencyCode?.toLowerCase() || "usd"}
                        </Badge>
                      </div>
                      <div className="mt-3 space-y-2">
                        <div className="flex justify-between text-sm opacity-90">
                          <span>Collection Fee:</span>
                          <span className="font-medium">{formatCurrency(orderDetail.collectionFee, orderDetail.currencyCode)}</span>
                        </div>
                        <div className="pt-2 border-t border-white/20 flex justify-between items-baseline">
                          <span className="text-xs font-bold uppercase">Total Price:</span>
                          <span className="text-2xl font-black">{formatCurrency(orderDetail.totalCost, orderDetail.currencyCode)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="rounded-xl border bg-gradient-to-b from-slate-50 to-white p-4 shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-muted-foreground text-xs uppercase tracking-wide">Order Status</p>
                <div className="flex items-center gap-4 mt-1">
                  <div className="flex-1 min-w-[140px]">
                    <Select value={statusDraft} onValueChange={setStatusDraft} disabled={isClientRole && orderDetail.orderStatus === "CANCELLED"}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Update Status" />
                      </SelectTrigger>
                      <SelectContent>
                        {isClientRole ? (
                          <>
                            {orderDetail.orderStatus !== "CANCELLED" && (
                              <SelectItem value="CANCELLED" className="text-red-600 font-medium">Cancel Order</SelectItem>
                            )}
                            <SelectItem value={orderDetail.orderStatus} disabled>{orderDetail.orderStatus}</SelectItem>
                          </>
                        ) : (
                          <>
                            <SelectItem value="SCHEDULED">Scheduled</SelectItem>
                            <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                            <SelectItem value="COMPLETED">Completed</SelectItem>
                            <SelectItem value="CANCELLED" className="text-red-600 font-medium">Cancelled</SelectItem>
                            <SelectItem value="NO_SHOW" className="text-orange-600 font-medium">No Show</SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    onClick={handleSaveStatus}
                    disabled={
                      updateOrderStatusResult.isLoading ||
                      statusDraft === orderDetail.orderStatus ||
                      (isClientRole && orderDetail.orderStatus === "CANCELLED")
                    }
                    className="px-6"
                  >
                    {updateOrderStatusResult.isLoading ? "Saving..." : "Save Status"}
                  </Button>
                </div>
                <div className="mt-2 flex items-center gap-3">
                  <Badge className={getStatusColor(orderDetail.orderStatus)}>{orderDetail.orderStatus}</Badge>
                  {isClientRole && orderDetail.orderStatus === "CANCELLED" && (
                    <p className="text-xs font-medium text-slate-500 italic">
                      To reactivate this order, please call or email Medigenix support.
                    </p>
                  )}
                </div>
              </div>
              {!isClientRole && (
                <div className="md:text-right">
                  <p className="text-muted-foreground text-xs uppercase tracking-wide">Total Cost</p>
                  <p className="text-3xl font-extrabold mt-1 text-slate-900">
                    {formatCurrency(orderDetail.totalCost, orderDetail.currencyCode)}
                  </p>
                  {(() => {
                    const collectionFee = orderDetail.collectionFee || 0;
                    if (collectionFee > 0) {
                      return (
                        <p className="text-xs font-medium text-slate-600 mt-1">
                          Include Collection fee: {formatCurrency(collectionFee, orderDetail.currencyCode)}
                        </p>
                      );
                    }
                    return (
                      <p className="text-xs text-muted-foreground mt-1">
                        Calculated from scheduled test pricing
                      </p>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>


      <Card>
        <CardHeader>
          <CardTitle>Tests</CardTitle>
        </CardHeader>
        <CardContent>
          {orderDetail.tests.length === 0 ? (
            <p className="text-sm text-muted-foreground">No tests found for this order.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Test Name</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Visit Date</TableHead>
                    {!isClientRole && <TableHead className="text-right">Price</TableHead>}
                    <TableHead className="min-w-[280px]">Result Document</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orderDetail.tests.map((test) => {
                    const hasDocument = !!test.resultDocumentUrl;
                    const testStatus = getTestWorkflowStatus(test);
                    return (
                      <TableRow key={test.appointmentId} className="hover:bg-slate-50/50">
                        <TableCell className="font-medium">
                          <div className="flex flex-col">
                            <span className="text-slate-900 font-bold">
                              {test.testName || "-"}
                              {test.aliasName && (
                                <span className="text-blue-600 ml-1 font-semibold">
                                  - {test.aliasName}
                                </span>
                              )}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={getTestWorkflowStatusColor(testStatus)}>
                            {getTestWorkflowStatusLabel(testStatus)}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatDateTime(test.scheduledAt)}</TableCell>
                        {!isClientRole && (
                          <TableCell className="text-right font-semibold">
                            {formatCurrency(test.testPrice, test.currencyCode ?? orderDetail.currencyCode)}
                          </TableCell>
                        )}
                        <TableCell>
                          {!isClientRole && !test.resultDocumentUrl && (
                            <div className="flex flex-col sm:flex-row items-center gap-2">
                              <div className="relative group">
                                <input
                                  type="file"
                                  className="absolute inset-0 opacity-0 cursor-pointer disabled:cursor-not-allowed"
                                  onChange={(e) => handleFileChange(test.appointmentId, e.target.files?.[0] || null)}
                                  accept=".pdf,.jpg,.jpeg,.png"
                                  disabled={savingAppointmentId === test.appointmentId}
                                />
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="gap-1.5 border-dashed border-slate-300 group-hover:border-blue-500 transition-colors"
                                  disabled={savingAppointmentId === test.appointmentId}
                                >
                                  <Upload className="w-3.5 h-3.5 text-slate-500 group-hover:text-blue-600" />
                                  <span className="text-xs">Choose File</span>
                                </Button>
                              </div>
                              {selectedFiles[test.appointmentId] && (
                                <Button
                                  size="sm"
                                  className="bg-blue-600 hover:bg-blue-700 h-8 px-3 transition-all animate-in fade-in slide-in-from-right-2"
                                  onClick={() => handleUploadResult(test)}
                                  disabled={savingAppointmentId === test.appointmentId}
                                >
                                  {savingAppointmentId === test.appointmentId ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  ) : (
                                    <span className="text-xs font-semibold">Upload</span>
                                  )}
                                </Button>
                              )}
                            </div>
                          )}
                          {hasDocument && (
                            <p className="text-xs text-emerald-600 font-medium flex items-center gap-1">
                              <CheckCircle2 className="w-3.5 h-3.5" /> Document Uploaded
                            </p>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-2">
                            {!isClientRole && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-slate-100">
                                    <Activity className="w-4 h-4 text-slate-400" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56">
                                  <DropdownMenuLabel>Test Actions</DropdownMenuLabel>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={() => handleManualTestWorkflowStatus(test, "IN_PROGRESS")}
                                    className="gap-2 cursor-pointer"
                                  >
                                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                                    <span>In Progress</span>
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => handleManualTestWorkflowStatus(test, "READY_TO_REVIEW")}
                                    className="gap-2 cursor-pointer"
                                  >
                                    <XCircle className="w-4 h-4 text-emerald-600" />
                                    <span>Mark Completed</span>
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => handleManualTestWorkflowStatus(test, "CANCELED")}
                                    className="gap-2 cursor-pointer"
                                  >
                                    <XCircle className="w-4 h-4 text-rose-600" />
                                    <span>Did Not Perform</span>
                                  </DropdownMenuItem>
                                  {test.resultDocumentUrl && (
                                    <DropdownMenuItem
                                      onClick={() => handleDeleteResult(test)}
                                      className="gap-2 text-rose-600 cursor-pointer hover:bg-rose-50"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                      <span>Delete Result</span>
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                            {hasDocument && (
                              <Button
                                size="sm"
                                variant="outline"
                                title="View uploaded document"
                                onClick={() => openDocumentPreview(test)}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            )}
                            {hasDocument && !isClientRole && (
                              <Button
                                size="sm"
                                variant="outline"
                                title="Delete uploaded document"
                                onClick={() => handleDeleteResult(test)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
